import { FileText, Banknote, Building2, Wallet, ClipboardCheck } from "lucide-react";
import EMICalculator from "@/components/EMICalculator";
import SectionHeading from "@/components/SectionHeading";

const HELP = [
  { icon: Wallet, title: "Loan Consultation", desc: "Right loan amount and tenure planning for your business." },
  { icon: Banknote, title: "Down-Payment Planning", desc: "Structure down-payment to keep EMIs comfortable." },
  { icon: FileText, title: "EMI Guidance", desc: "Understand exactly what you pay each month, upfront." },
  { icon: ClipboardCheck, title: "Documentation Support", desc: "We prepare, verify and submit all documents for you." },
  { icon: Building2, title: "Bank Coordination", desc: "Direct coordination with 12+ banks and NBFCs." },
];

const DOCS_INDIVIDUAL = ["Aadhaar Card", "PAN Card", "Address Proof", "Bank Statement (6 months)", "Income Documents"];
const DOCS_BUSINESS = ["GST Certificate (if applicable)", "UDYAM Registration (if applicable)", "Bank Statement (6–12 months)", "Business Proof / Establishment Certificate"];

export default function Finance() {
  return (
    <div data-testid="finance-page">
      <section className="relative pt-32 pb-12 md:pt-40 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] radial-glow-red pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-red-500 mb-4 font-mono-brand">
            <span className="w-8 h-px bg-current" /> Vehicle Finance
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] text-white">
            Transparent Finance. <span className="text-gradient-red">Faster Approvals.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-3xl leading-relaxed">
            We work with 12+ leading banks and NBFCs to help you secure the right
            commercial vehicle loan. Approvals subject to bank eligibility.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <EMICalculator />
        </div>
      </section>

      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow="What We Help You With" title="Complete Finance Assistance" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {HELP.map((h, i) => (
              <div key={h.title} className="glass glass-hover rounded-2xl p-6" data-testid={`finance-help-${i}`}>
                <h.icon className="w-6 h-6 text-red-500 mb-4" />
                <div className="font-display font-bold text-white text-base">{h.title}</div>
                <div className="text-sm text-slate-400 mt-2 leading-relaxed">{h.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass rounded-3xl p-8 md:p-10" data-testid="finance-docs-individual">
            <div className="text-xs uppercase tracking-[0.28em] text-red-500 font-mono-brand mb-3">Required — Individual</div>
            <h3 className="font-display text-2xl font-bold text-white mb-6">Documents for Individuals</h3>
            <ul className="space-y-3">
              {DOCS_INDIVIDUAL.map((d) => (
                <li key={d} className="flex items-center gap-3 text-slate-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
          <div className="glass rounded-3xl p-8 md:p-10" data-testid="finance-docs-business">
            <div className="text-xs uppercase tracking-[0.28em] text-red-500 font-mono-brand mb-3">Required — Business</div>
            <h3 className="font-display text-2xl font-bold text-white mb-6">Documents for Businesses</h3>
            <ul className="space-y-3">
              {DOCS_BUSINESS.map((d) => (
                <li key={d} className="flex items-center gap-3 text-slate-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
